import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Megaphone, AlertOctagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RemarketingTab } from "@/components/intelligence/RemarketingTab";
import { CollectionTab } from "@/components/intelligence/CollectionTab";
import { TabErrorBoundary } from "@/components/intelligence/TabErrorBoundary";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { toast } from "sonner";

const Intelligence = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const queryClient = useQueryClient();
  const { isPro, isLoading: subLoading } = useSubscriptionLimits();

  useEffect(() => {
    if (!user || subLoading) return;
    if (!isPro) {
      toast.error("Acesso restrito ao plano PRO. Assine para acessar a Central Inteligente.");
      navigate("/subscription");
    }
  }, [user, isPro, subLoading, navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Atualização automática: revalida listas quando há mudança em loans/payments/clients
  useEffect(() => {
    if (!user?.id) return;
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ["intel-loans", user.id] });
      queryClient.invalidateQueries({ queryKey: ["intel-payments", user.id] });
      queryClient.invalidateQueries({ queryKey: ["intel-clients", user.id] });
    };
    const channel = supabase
      .channel(`intel-realtime-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "loans", filter: `user_id=eq.${user.id}` },
        refresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "loan_payments", filter: `user_id=eq.${user.id}` },
        refresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "clients", filter: `user_id=eq.${user.id}` },
        refresh
      )
      .subscribe();

    // Recalcula atrasos quando volta o foco/aba (cliente vira "overdue" pela passagem do dia)
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user?.id, queryClient]);

  const { data: loansData = [] } = useQuery({
    queryKey: ["intel-loans", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loans")
        .select(`*, clients!loans_client_id_fkey(name, phone, cpf)`)
        .eq("user_id", user.id);
      if (error) throw error;
      return data;
    },
  });

  const { data: paymentsData = [] } = useQuery({
    queryKey: ["intel-payments", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loan_payments")
        .select("loan_id, paid_at, interest_amount, principal_amount, amount")
        .eq("user_id", user.id);
      if (error) throw error;
      return data;
    },
  });

  const { data: clientsData = [] } = useQuery({
    queryKey: ["intel-clients", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name, phone, created_at")
        .eq("user_id", user.id);
      if (error) throw error;
      return data;
    },
  });

  if (!user) return null;

  const parseLocalDate = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const loans = (loansData as any[]).map((l) => ({
    id: l.id,
    clientId: l.client_id,
    clientName: l.clients?.name ?? "Cliente",
    phone: l.clients?.phone ?? null,
    amount: Number(l.amount),
    interestRate: Number(l.interest_rate),
    interestType: l.interest_type as "monthly" | "fixed" | "daily",
    status: l.status as "paid" | "active" | "overdue",
    dueDate: parseLocalDate(l.due_date),
    loanDate: parseLocalDate(l.loan_date),
    updatedAt: new Date(l.updated_at),
  }));

  const payments = (paymentsData as any[]).map((p) => ({
    loanId: p.loan_id,
    paidAt: new Date(p.paid_at),
    interestAmount: Number(p.interest_amount),
    principalAmount: Number(p.principal_amount),
    amount: Number(p.amount),
  }));

  const clients = (clientsData as any[]).map((c) => ({
    id: c.id,
    name: c.name,
    phone: c.phone ?? null,
    createdAt: new Date(c.created_at),
  }));

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="animate-fade-in flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="shrink-0 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="relative h-12 w-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.55)]">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-display font-semibold tracking-tight">
                Central Inteligente
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Oportunidades de novos contratos e recuperação de inadimplentes
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="remarketing" className="w-full animate-fade-in">
          <TabsList className="grid grid-cols-2 w-full max-w-md h-11 bg-muted border border-border">
            <TabsTrigger value="remarketing" className="gap-2">
              <Megaphone className="h-4 w-4" />
              Remarketing
            </TabsTrigger>
            <TabsTrigger value="collection" className="gap-2">
              <AlertOctagon className="h-4 w-4" />
              Cobrança
            </TabsTrigger>
          </TabsList>

          <TabsContent value="remarketing" className="mt-6">
            <TabErrorBoundary>
              <RemarketingTab loans={loans} payments={payments} clients={clients} />
            </TabErrorBoundary>
          </TabsContent>
          <TabsContent value="collection" className="mt-6">
            <TabErrorBoundary>
              <CollectionTab loans={loans} payments={payments} />
            </TabErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Intelligence;